/*
 * Description:
 * ---------------------
 * Deep Telemetry — the operator-grade drill-down view.
 *
 * Inspired by the offline benchmark harness under testing/ (per-core CPU
 * grid, softnet drops, NIC IRQ heatmap, thermal-zone overlay), this panel
 * pulls the new fine-grained metrics from notabox-server's telemetry API:
 *
 *   cpu_core       /proc/stat per-CPU utilisation slices
 *   netdev         /proc/net/dev per-interface counters (errors, drops)
 *   softnet        /proc/net/softnet_stat per-CPU (dropped, squeezed)
 *   irq            /proc/interrupts per-NIC imbalance score
 *   thermal_zone   /sys/class/thermal/thermal_zone* temperatures
 *
 * Every chart has a "Download CSV" button that streams the same series
 * out of /api/v1/telemetry/export.csv — the live counterpart of the
 * timeseries.csv file produced by testing/run.py.
 *
 * ! file DeepTelemetryPanel.js
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2026
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { WanLabel } from "./WanNames";
import { fmtBytes, fmtNum } from "../utils/formatters";
import useVisibleInterval from "../hooks/useVisibleInterval";
import DownloadButton from "./DownloadButton";

// ── Window choices (matches PilotInsightsPanel) ─────────────────────────────

const WINDOWS = [
  { key: "5m", label: "Last 5 m", seconds: 5 * 60 },
  { key: "30m", label: "Last 30 m", seconds: 30 * 60 },
  { key: "1h", label: "Last 1 h", seconds: 3600 },
  { key: "6h", label: "Last 6 h", seconds: 6 * 3600 },
  { key: "24h", label: "Last 24 h", seconds: 24 * 3600 },
];

// A categorical palette wide enough for 16 cores / IRQs.
const PALETTE = [
  "#2563eb", "#dc2626", "#16a34a", "#d97706", "#9333ea", "#0891b2",
  "#db2777", "#65a30d", "#ea580c", "#0284c7", "#7c3aed", "#059669",
  "#e11d48", "#ca8a04", "#1d4ed8", "#be123c",
];

// ── Small primitives ────────────────────────────────────────────────────────

function Card({ title, subtitle, right, children, accent }) {
  return (
    <div
      style={{
        background: "white",
        borderRadius: 12,
        border: "1px solid #e2e8f0",
        padding: "1.1rem 1.25rem",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        borderTop: accent ? `3px solid ${accent}` : undefined,
        display: "flex",
        flexDirection: "column",
        gap: "0.65rem",
      }}
    >
      {(title || subtitle || right) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem" }}>
          <div>
            {title && (
              <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#475569", letterSpacing: "0.02em", textTransform: "uppercase" }}>
                {title}
              </div>
            )}
            {subtitle && <div style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: 2 }}>{subtitle}</div>}
          </div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

function MultiLine({ series, height = 180, yLabel = "", yMaxFixed }) {
  // series: [{ name, color, points: [{ts(ms), value}] }]
  const all = series.flatMap((s) => s.points).filter((p) => p.value != null && isFinite(p.value));
  if (all.length === 0) {
    return (
      <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "0.85rem" }}>
        No data in this window.
      </div>
    );
  }
  const xs = all.map((p) => p.ts);
  const ys = all.map((p) => p.value);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = 0;
  const yMax = yMaxFixed != null ? yMaxFixed : Math.max(...ys) * 1.15 || 1;
  const w = 720;
  const h = height;
  const pad = { l: 44, r: 12, t: 10, b: 22 };
  const sx = (x) => pad.l + ((x - xMin) / Math.max(1, xMax - xMin)) * (w - pad.l - pad.r);
  const sy = (y) => h - pad.b - ((y - yMin) / Math.max(0.0001, yMax - yMin)) * (h - pad.t - pad.b);

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height, display: "block" }}>
        {[0, 0.25, 0.5, 0.75, 1].map((g, i) => {
          const y = pad.t + g * (h - pad.t - pad.b);
          return (
            <g key={i}>
              <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke="#f1f5f9" />
              <text x={4} y={y + 3} fontSize="9" fill="#94a3b8">
                {Number((yMax * (1 - g)).toFixed(2))}
              </text>
            </g>
          );
        })}
        {yLabel && <text x={4} y={h - 6} fontSize="9" fill="#94a3b8">{yLabel}</text>}
        <text x={pad.l} y={h - 6} fontSize="9" fill="#94a3b8">
          {new Date(xMin).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </text>
        <text x={w - pad.r - 40} y={h - 6} fontSize="9" fill="#94a3b8">
          {new Date(xMax).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </text>
        {series.map((s, i) => {
          const pts = s.points.filter((p) => p.value != null && isFinite(p.value));
          if (pts.length < 2) return null;
          const d = pts
            .map((p, j) => `${j === 0 ? "M" : "L"}${sx(p.ts).toFixed(1)} ${sy(p.value).toFixed(1)}`)
            .join(" ");
          return <path key={i} d={d} fill="none" stroke={s.color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />;
        })}
      </svg>
    </div>
  );
}

function Legend({ items }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem 0.9rem", fontSize: "0.72rem", color: "#475569" }}>
      {items.map((it, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ display: "inline-block", width: 10, height: 3, background: it.color, borderRadius: 1 }} />
          {it.name}
        </span>
      ))}
    </div>
  );
}

// ── Sample-to-series helpers ────────────────────────────────────────────────

// groupBy takes raw samples and returns { tagValue: [{ts, value}] } using
// the supplied tag key and field name.
function groupBy(samples, tagKey, fieldName) {
  const out = {};
  for (const s of samples || []) {
    const tag = s.tags?.[tagKey];
    if (!tag) continue;
    const v = s.fields?.[fieldName];
    if (v == null) continue;
    const arr = out[tag] || (out[tag] = []);
    arr.push({ ts: new Date(s.ts).getTime(), value: v });
  }
  for (const k of Object.keys(out)) out[k].sort((a, b) => a.ts - b.ts);
  return out;
}

function deltaSeries(points) {
  // Convert a cumulative-counter series into a per-sample delta. Drops
  // counter-reset rows (where current < prev).
  const out = [];
  for (let i = 1; i < points.length; i++) {
    const d = points[i].value - points[i - 1].value;
    if (d < 0) continue;
    out.push({ ts: points[i].ts, value: d });
  }
  return out;
}

function totalDelta(points) {
  if (!points || points.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const d = points[i].value - points[i - 1].value;
    if (d > 0) total += d;
  }
  return total;
}

// Sort keys "cpu0, cpu1, ..., cpu10" numerically.
function sortCpuKeys(keys) {
  return [...keys].sort((a, b) => {
    const ai = parseInt(String(a).replace(/[^0-9]/g, ""), 10);
    const bi = parseInt(String(b).replace(/[^0-9]/g, ""), 10);
    return ai - bi;
  });
}

// ── Panel ───────────────────────────────────────────────────────────────────

export default function DeepTelemetryPanel({ selectedNode, serverApiUrl }) {
  const [win, setWin] = useState("30m");
  const [cpu, setCpu] = useState([]);
  const [netdev, setNetdev] = useState([]);
  const [softnet, setSoftnet] = useState([]);
  const [irq, setIrq] = useState([]);
  const [thermal, setThermal] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const window = useMemo(() => WINDOWS.find((w) => w.key === win) || WINDOWS[1], [win]);

  const csvHref = useCallback(
    (metric) => {
      if (!selectedNode || !serverApiUrl) return "";
      const since = new Date(Date.now() - window.seconds * 1000).toISOString();
      return serverApiUrl(`/telemetry/export.csv?node=${encodeURIComponent(selectedNode)}&metric=${metric}&since=${encodeURIComponent(since)}`);
    },
    [selectedNode, serverApiUrl, window.seconds],
  );

  const load = useCallback(async () => {
    if (!selectedNode || !serverApiUrl) return;
    setLoading(true);
    setErr(null);
    const since = new Date(Date.now() - window.seconds * 1000).toISOString();
    const base = (m, limit = 5000) =>
      serverApiUrl(`/telemetry/timeseries?node=${encodeURIComponent(selectedNode)}&metric=${m}&since=${encodeURIComponent(since)}&limit=${limit}`);
    try {
      const [cpuR, ndR, snR, irR, thR] = await Promise.all([
        fetchWithTimeout(base("cpu_core")),
        fetchWithTimeout(base("netdev")),
        fetchWithTimeout(base("softnet")),
        fetchWithTimeout(base("irq")),
        fetchWithTimeout(base("thermal_zone")),
      ]);
      const [cpuJ, ndJ, snJ, irJ, thJ] = await Promise.all([
        cpuR.json(), ndR.json(), snR.json(), irR.json(), thR.json(),
      ]);
      setCpu(cpuJ.samples || []);
      setNetdev(ndJ.samples || []);
      setSoftnet(snJ.samples || []);
      setIrq(irJ.samples || []);
      setThermal(thJ.samples || []);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [selectedNode, serverApiUrl, window.seconds]);

  useEffect(() => { load(); }, [load]);
  useVisibleInterval(load, 15000);

  // ── Per-core CPU series ───────────────────────────────────────────
  const cpuSeries = useMemo(() => {
    const groups = groupBy(cpu, "cpu", "util_pct");
    const keys = sortCpuKeys(Object.keys(groups));
    return keys.map((k, i) => ({
      name: k,
      color: PALETTE[i % PALETTE.length],
      points: groups[k],
    }));
  }, [cpu]);

  // ── Softnet drops cumulative series ───────────────────────────────
  const softnetSeries = useMemo(() => {
    const groups = groupBy(softnet, "cpu", "dropped");
    const keys = sortCpuKeys(Object.keys(groups));
    return keys.map((k, i) => ({
      name: k,
      color: PALETTE[i % PALETTE.length],
      points: groups[k],
    }));
  }, [softnet]);

  const softnetDeltas = useMemo(() => {
    const groups = groupBy(softnet, "cpu", "dropped");
    const sum = {};
    for (const cpu of Object.keys(groups)) {
      sum[cpu] = totalDelta(groups[cpu]);
    }
    return sum;
  }, [softnet]);

  // ── Per-NIC counters latest + deltas ──────────────────────────────
  const netdevRows = useMemo(() => {
    const ifaces = {};
    for (const s of netdev) {
      const iface = s.tags?.iface;
      if (!iface) continue;
      const arr = ifaces[iface] || (ifaces[iface] = []);
      arr.push(s);
    }
    const rows = [];
    for (const iface of Object.keys(ifaces).sort()) {
      const arr = ifaces[iface].sort((a, b) => new Date(a.ts) - new Date(b.ts));
      const first = arr[0];
      const last = arr[arr.length - 1];
      const delta = (k) => Math.max(0, (last.fields?.[k] || 0) - (first.fields?.[k] || 0));
      rows.push({
        iface,
        rx_bytes: delta("rx_bytes"),
        tx_bytes: delta("tx_bytes"),
        rx_dropped: delta("rx_dropped"),
        tx_dropped: delta("tx_dropped"),
        rx_errors: delta("rx_errors"),
        tx_errors: delta("tx_errors"),
        last_ts: last.ts,
      });
    }
    return rows;
  }, [netdev]);

  // ── Thermal zone series ───────────────────────────────────────────
  const thermalSeries = useMemo(() => {
    const groups = {};
    for (const s of thermal) {
      const zone = s.tags?.type || s.tags?.zone;
      if (!zone) continue;
      const v = s.fields?.temp_c;
      if (v == null) continue;
      const arr = groups[zone] || (groups[zone] = []);
      arr.push({ ts: new Date(s.ts).getTime(), value: v });
    }
    return Object.keys(groups).sort().map((k, i) => ({
      name: k,
      color: PALETTE[i % PALETTE.length],
      points: groups[k].sort((a, b) => a.ts - b.ts),
    }));
  }, [thermal]);

  // ── IRQ imbalance — latest snapshot per device ────────────────────
  const irqRows = useMemo(() => {
    const byDev = {};
    for (const s of irq) {
      const dev = s.tags?.device;
      if (!dev) continue;
      const arr = byDev[dev] || (byDev[dev] = []);
      arr.push(s);
    }
    const rows = [];
    for (const dev of Object.keys(byDev).sort()) {
      const arr = byDev[dev].sort((a, b) => new Date(a.ts) - new Date(b.ts));
      const last = arr[arr.length - 1];
      rows.push({
        device: dev,
        irq: last.tags?.irq,
        imbalance: last.fields?.imbalance || 0,
        total: last.fields?.total || 0,
        num_cpus: last.fields?.num_cpus || 0,
      });
    }
    return rows;
  }, [irq]);

  if (!selectedNode) {
    return (
      <div style={{ padding: "1.25rem", color: "#64748b" }}>
        Select an edge to inspect its deep telemetry.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "1rem 1.25rem" }}>
      {/* Header */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "0.75rem" }}>
        <div>
          <div style={{ fontSize: "1.15rem", fontWeight: 700, color: "#0f172a" }}>Deep Telemetry</div>
          <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
            Per-core CPU, NIC counters, softnet drops, IRQ distribution and thermal zones — sourced from the live telemetry pipeline.
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.4rem" }}>
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              onClick={() => setWin(w.key)}
              style={{
                padding: "0.35rem 0.7rem",
                borderRadius: 6,
                border: "1px solid " + (win === w.key ? "#2563eb" : "#cbd5e1"),
                background: win === w.key ? "#2563eb" : "white",
                color: win === w.key ? "white" : "#334155",
                fontSize: "0.78rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {err && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", borderRadius: 8, padding: "0.6rem 0.9rem", fontSize: "0.85rem" }}>
          Telemetry fetch error: {err}
        </div>
      )}
      {loading && !err && (
        <div style={{ color: "#64748b", fontSize: "0.78rem" }}>Loading…</div>
      )}

      {/* Per-core CPU */}
      <Card
        title="Per-core CPU utilisation"
        subtitle={`${cpuSeries.length} cores · % busy`}
        accent="#2563eb"
        right={<DownloadButton href={csvHref("cpu_core")} label="Download CSV" />}
      >
        <MultiLine series={cpuSeries} height={220} yLabel="%" yMaxFixed={100} />
        <Legend items={cpuSeries} />
      </Card>

      {/* Softnet drops */}
      <Card
        title="Softnet drops (per-CPU, cumulative)"
        subtitle="Kernel NAPI rx-queue overflow — non-zero deltas indicate kernel-side packet loss."
        accent="#dc2626"
        right={<DownloadButton href={csvHref("softnet")} label="Download CSV" />}
      >
        <MultiLine series={softnetSeries} height={180} yLabel="dropped" />
        <Legend items={softnetSeries} />
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.45rem", marginTop: 4 }}>
          {sortCpuKeys(Object.keys(softnetDeltas)).map((c) => (
            <span
              key={c}
              style={{
                fontSize: "0.7rem",
                padding: "0.2rem 0.45rem",
                borderRadius: 4,
                background: softnetDeltas[c] > 0 ? "#fef2f2" : "#f1f5f9",
                color: softnetDeltas[c] > 0 ? "#b91c1c" : "#475569",
                fontWeight: 600,
              }}
            >
              {c}: +{fmtNum(softnetDeltas[c])}
            </span>
          ))}
        </div>
      </Card>

      {/* Per-NIC counters */}
      <Card
        title="Per-interface counters (window deltas)"
        subtitle="Hardware-level rx/tx; non-zero drop/error columns are the signal you want to see go down."
        accent="#16a34a"
        right={<DownloadButton href={csvHref("netdev")} label="Download CSV" />}
      >
        {netdevRows.length === 0 ? (
          <div style={{ color: "#94a3b8", fontSize: "0.85rem" }}>No netdev samples yet.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr style={{ background: "#f8fafc", color: "#475569", textAlign: "left" }}>
                  <th style={th}>Interface</th>
                  <th style={thNum}>Rx bytes</th>
                  <th style={thNum}>Tx bytes</th>
                  <th style={thNum}>Rx drops</th>
                  <th style={thNum}>Tx drops</th>
                  <th style={thNum}>Rx errors</th>
                  <th style={thNum}>Tx errors</th>
                </tr>
              </thead>
              <tbody>
                {netdevRows.map((r) => (
                  <tr key={r.iface} style={{ borderTop: "1px solid #e2e8f0" }}>
                    <td style={td}><WanLabel iface={r.iface} /></td>
                    <td style={tdNum}>{fmtBytes(r.rx_bytes)}</td>
                    <td style={tdNum}>{fmtBytes(r.tx_bytes)}</td>
                    <td style={{ ...tdNum, color: r.rx_dropped > 0 ? "#b91c1c" : "#475569", fontWeight: r.rx_dropped > 0 ? 700 : 400 }}>{fmtNum(r.rx_dropped)}</td>
                    <td style={{ ...tdNum, color: r.tx_dropped > 0 ? "#b91c1c" : "#475569", fontWeight: r.tx_dropped > 0 ? 700 : 400 }}>{fmtNum(r.tx_dropped)}</td>
                    <td style={{ ...tdNum, color: r.rx_errors > 0 ? "#b91c1c" : "#475569", fontWeight: r.rx_errors > 0 ? 700 : 400 }}>{fmtNum(r.rx_errors)}</td>
                    <td style={{ ...tdNum, color: r.tx_errors > 0 ? "#b91c1c" : "#475569", fontWeight: r.tx_errors > 0 ? 700 : 400 }}>{fmtNum(r.tx_errors)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* IRQ imbalance */}
      <Card
        title="NIC IRQ distribution"
        subtitle="Fraction of interrupts handled by the busiest CPU. 1.0 means a single core absorbs every IRQ — a classic bonding bottleneck."
        accent="#9333ea"
        right={<DownloadButton href={csvHref("irq")} label="Download CSV" />}
      >
        {irqRows.length === 0 ? (
          <div style={{ color: "#94a3b8", fontSize: "0.85rem" }}>No IRQ samples yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
            {irqRows.map((r) => {
              const bad = r.imbalance > 0.8;
              const ok = r.imbalance < 0.5;
              const color = bad ? "#dc2626" : ok ? "#16a34a" : "#d97706";
              return (
                <div key={r.device + r.irq}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, color: "#0f172a" }}>
                      {r.device} <span style={{ color: "#94a3b8", fontWeight: 400, marginLeft: 4 }}>irq {r.irq}</span>
                    </span>
                    <span style={{ color: "#475569" }}>
                      imbalance <b style={{ color }}>{(r.imbalance * 100).toFixed(0)}%</b>
                      <span style={{ color: "#94a3b8", marginLeft: 8 }}>{fmtNum(r.total)} ints · {r.num_cpus} cpus</span>
                    </span>
                  </div>
                  <div style={{ width: "100%", height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${Math.min(100, r.imbalance * 100)}%`, height: "100%", background: color }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Thermal */}
      <Card
        title="Thermal zones"
        subtitle="One trace per /sys/class/thermal zone (°C)."
        accent="#ea580c"
        right={<DownloadButton href={csvHref("thermal_zone")} label="Download CSV" />}
      >
        <MultiLine series={thermalSeries} height={180} yLabel="°C" />
        <Legend items={thermalSeries} />
      </Card>
    </div>
  );
}

const th = { padding: "0.5rem 0.75rem", fontWeight: 600 };
const thNum = { ...th, textAlign: "right" };
const td = { padding: "0.5rem 0.75rem", color: "#334155" };
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };
